import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import NotificationToast from '../../../components/ui/NotificationToast';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { formatTimeFromApi } from '../../../utils/attendanceDateTime';
import {
  toLocalDateStr,
  monthDateRange,
  computeSummaryFromRecords,
  normalizeRecordDate,
  groupRecordsByMonth,
} from '../../../utils/attendanceHelpers';

const AttendanceDetails = () => {
  const { employee, reloadAttendance } = useEmployeeSelfService();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [backendSummary, setBackendSummary] = useState(null);
  const [summary, setSummary] = useState({
    totalDays: 0,
    presentDays: 0,
    absentDays: 0,
    totalHours: '0h 0m',
    avgDailyHours: '0h 0m',
    attendanceRate: 0,
  });
  const [filters, setFilters] = useState(() => {
    const { start, end } = monthDateRange();
    return { date_from: start, date_to: end, status: 'all' };
  });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (!employee?.id) return;
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth();
    const { start, end } = monthDateRange(y, m);
    setFilters((prev) => {
      if (prev.date_from === start && prev.date_to === end) return prev;
      return { ...prev, date_from: start, date_to: end };
    });
  }, [selectedDate, employee?.id]);

  useEffect(() => {
    if (employee?.id) {
      loadAttendanceData();
    }
  }, [employee, filters]);

  useEffect(() => {
    const getWeekDates = (date) => {
      const week = [];
      const current = new Date(date);
      const day = current.getDay();
      const diff = current.getDate() - day;

      for (let i = 0; i < 7; i++) {
        const weekDate = new Date(current);
        weekDate.setDate(diff + i);
        week.push(weekDate);
      }
      return week;
    };
    setWeekDates(getWeekDates(selectedDate));
  }, [selectedDate]);

  const loadAttendanceData = async () => {
    if (!employee?.id) return;

    try {
      setLoading(true);
      const startDate = filters.date_from;
      const endDate = filters.date_to;

      const data = await reloadAttendance(startDate, endDate);
      if (data) {
        let records = data.records || [];
        if (filters.status !== 'all') {
          records = records.filter((r) => (r.status || 'absent') === filters.status);
        }
        setAttendanceRecords(records);
        setBackendSummary(data.summary);
        setSummary(computeSummaryFromRecords(data.records || []));
      } else {
        setAttendanceRecords([]);
        setBackendSummary(null);
        setSummary(computeSummaryFromRecords([]));
      }
    } catch (err) {
      setNotification({ message: 'Failed to load attendance data', type: 'error' });
      setAttendanceRecords([]);
      setSummary(computeSummaryFromRecords([]));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString, attendanceDate = '') => {
    if (!dateString) return null;
    const wall = formatTimeFromApi(dateString, attendanceDate);
    if (wall && wall !== '-') return wall;
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getAttendanceStatus = (date) => {
    const dateStr = toLocalDateStr(date);
    // Normalize attendance_date for comparison (handle both date and datetime formats)
    const record = attendanceRecords.find((r) => normalizeRecordDate(r) === dateStr);
    if (!record) return null;
    
    return {
      punchIn: formatTime(record.clock_in_time, record.attendance_date),
      punchOut: formatTime(record.clock_out_time, record.attendance_date),
      duration: formatDuration(record.work_hours)
    };
  };

  const transformRecord = (record) => {
    if (!record) return null;
    
    const hasPunchIn = !!record.clock_in_time;
    const hasPunchOut = !!record.clock_out_time;
    const status = hasPunchIn && hasPunchOut ? 'complete' : hasPunchIn ? 'incomplete' : 'absent';

    const attendanceDate = record.attendance_date ? record.attendance_date.split('T')[0] : null;

    const formatPunchTimes = (type) => {
      if (!record.punches?.length) return null;
      const times = record.punches
        .filter((p) => p.punch_type === type)
        .map((p) => formatTime(p.punch_time, record.attendance_date));
      return times.length ? times.join(', ') : null;
    };

    const punchInTimes = formatPunchTimes('IN');
    const punchOutTimes = formatPunchTimes('OUT');
    const sessionCount = record.punches?.length
      ? Math.ceil(record.punches.filter((p) => p.punch_type === 'IN').length)
      : 0;

    return {
      id: record.id,
      date: attendanceDate ? formatDate(attendanceDate) : 'Date not available',
      rawDate: attendanceDate,
      status,
      sessionCount,
      totalDuration: formatDuration(record.work_hours),
      punchIn: hasPunchIn ? {
        time: punchInTimes || formatTime(record.clock_in_time, record.attendance_date),
        location: record.clock_in_location_address || record.clock_in_location || 'Location not available',
      } : null,
      punchOut: hasPunchOut || punchOutTimes ? {
        time: punchOutTimes || (hasPunchOut ? formatTime(record.clock_out_time, record.attendance_date) : '-'),
        location: record.clock_out_location_address || record.clock_out_location || 'Location not available',
      } : null,
      adminNotes: record.notes || null
    };
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const formatRecordStatus = (backendStatus, computedStatus) => {
    if (backendStatus === 'present' || backendStatus === 'late' || backendStatus === 'half_day') {
      return computedStatus === 'complete' ? 'Complete' : computedStatus === 'incomplete' ? 'Incomplete' : backendStatus.replace('_', ' ');
    }
    if (backendStatus === 'absent') return 'Absent';
    if (computedStatus === 'complete') return 'Complete';
    if (computedStatus === 'incomplete') return 'Incomplete';
    return 'Absent';
  };

  const getRecordStatusClass = (backendStatus, computedStatus) => {
    if (backendStatus === 'present' || computedStatus === 'complete') return 'bg-green-100 text-green-800';
    if (backendStatus === 'late') return 'bg-amber-100 text-amber-800';
    if (backendStatus === 'half_day') return 'bg-blue-100 text-blue-800';
    if (backendStatus === 'absent' || computedStatus === 'absent') return 'bg-red-100 text-red-800';
    if (computedStatus === 'incomplete') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const selectedMonthValue = filters.date_from?.slice(0, 7) || '';

  const monthGroups = useMemo(
    () => groupRecordsByMonth(attendanceRecords),
    [attendanceRecords]
  );

  const handleMonthChange = (monthValue) => {
    if (!monthValue) return;
    const [y, m] = monthValue.split('-').map(Number);
    const { start, end } = monthDateRange(y, m - 1);
    setSelectedDate(new Date(y, m - 1, 1));
    setFilters((prev) => ({ ...prev, date_from: start, date_to: end }));
  };

  const shiftMonth = (delta) => {
    const base = selectedMonthValue ? new Date(`${selectedMonthValue}-01T12:00:00`) : new Date();
    base.setMonth(base.getMonth() + delta);
    const monthValue = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
    handleMonthChange(monthValue);
  };

  if (loading && !employee) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
            Attendance Details
          </h1>
          <p className="text-xs md:text-sm lg:text-base text-muted-foreground">
            Same records as Employee Management → Attendance Reports (filter by employee & month)
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={loadAttendanceData} disabled={loading}>
          Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h2 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">Attendance Summary</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground mb-1">{summary.totalDays}</p>
            <p className="text-sm text-muted-foreground">Total Days</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <p className="text-3xl font-bold text-foreground mb-1">{summary.presentDays}</p>
            <p className="text-sm text-muted-foreground">Present Days</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mb-3">
              <Icon name="XCircle" size={20} className="text-error" />
            </div>
            <p className="text-3xl font-bold text-foreground mb-1">{summary.absentDays}</p>
            <p className="text-sm text-muted-foreground">Absent Days</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-3">
              <Icon name="Clock" size={20} className="text-accent" />
            </div>
            <p className="text-3xl font-bold text-foreground mb-1">{summary.totalHours}</p>
            <p className="text-sm text-muted-foreground">Total Hours</p>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm text-muted-foreground">Average Daily Hours</span>
            <span className="text-lg font-semibold text-foreground">{summary.avgDailyHours}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm text-muted-foreground">Attendance Rate</span>
            <span className="text-lg font-semibold text-success">{summary.attendanceRate}%</span>
          </div>
        </div>
      </div>

      {/* Weekly Calendar */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6 gap-2">
          <h2 className="text-base md:text-lg font-semibold text-foreground">Weekly Attendance</h2>
          <div className="flex items-center gap-1 md:gap-2">
            <button
              onClick={handlePreviousWeek}
              className="p-1.5 md:p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronLeft" size={18} className="md:w-5 md:h-5" />
            </button>
            <span className="text-xs md:text-sm font-medium text-muted-foreground px-1 md:px-2 whitespace-nowrap">
              {weekDates[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <button
              onClick={handleNextWeek}
              className="p-1.5 md:p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronRight" size={18} className="md:w-5 md:h-5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 md:gap-2">
          {weekDates.map((date, index) => {
            const attendance = getAttendanceStatus(date);
            const selected = isSelected(date);
            const today = isToday(date);

            return (
              <button
                key={index}
                onClick={() => handleDateSelect(date)}
                className={`relative p-3 rounded-lg transition-colors ${
                  selected ? 'bg-primary text-primary-foreground shadow-lg' : 'bg-muted hover:bg-muted/80'
                } ${today && !selected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
              >
                <div className="text-xs font-medium mb-1">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className={`text-xl font-semibold mb-2 ${selected ? 'text-primary-foreground' : 'text-foreground'}`}>
                  {date.getDate()}
                </div>
                {attendance && (
                  <div className="space-y-1">
                    <div className={`flex items-center justify-center gap-1 ${selected ? 'text-primary-foreground' : 'text-success'}`}>
                      <Icon name="LogIn" size={12} />
                      <span className="text-xs">{attendance.punchIn}</span>
                    </div>
                    {attendance.punchOut && (
                      <div className={`flex items-center justify-center gap-1 ${selected ? 'text-primary-foreground' : 'text-error'}`}>
                        <Icon name="LogOut" size={12} />
                        <span className="text-xs">{attendance.punchOut}</span>
                      </div>
                    )}
                    <div className={`text-xs font-medium mt-1 ${selected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                      {attendance.duration}
                    </div>
                  </div>
                )}
                {!attendance && (
                  <div className={`text-xs ${selected ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                    No data
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h2 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4">Filter & Export</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          <Input
            type="month"
            label="Month"
            value={selectedMonthValue}
            onChange={(e) => handleMonthChange(e.target.value)}
          />
          <Input
            type="date"
            label="Date From"
            value={filters.date_from}
            onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
          />
          <Input
            type="date"
            label="Date To"
            value={filters.date_to}
            onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            options={[
              { value: 'all', label: 'All' },
              { value: 'present', label: 'Present' },
              { value: 'absent', label: 'Absent' },
              { value: 'late', label: 'Late' }
            ]}
          />
          <div className="flex items-end">
            <Button onClick={loadAttendanceData} iconName="Search" iconPosition="left">
              View Report
            </Button>
          </div>
        </div>
      </div>

      {/* Detailed Records */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-foreground">Detailed Records</h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              Month-wise attendance for {selectedMonthValue
                ? new Date(`${selectedMonthValue}-01T12:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
                : 'selected period'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
              aria-label="Previous month"
            >
              <Icon name="ChevronLeft" size={18} />
            </button>
            <Input
              type="month"
              value={selectedMonthValue}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="w-[160px]"
            />
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
              aria-label="Next month"
            >
              <Icon name="ChevronRight" size={18} />
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : attendanceRecords.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No attendance records found</p>
            <p className="text-xs text-muted-foreground mt-2">Try adjusting the date range or filters</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Month</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Punch In</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Punch Out</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Duration</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {monthGroups.map((group) => (
                    <React.Fragment key={group.monthKey}>
                      <tr className="bg-muted/60 border-t border-border">
                        <td colSpan={7} className="px-4 py-2">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <span className="font-semibold text-foreground">{group.monthLabel}</span>
                            <span className="text-xs text-muted-foreground">
                              Present: {group.summary.presentDays} • Absent: {group.summary.absentDays} • Hours: {group.summary.totalHours} • Rate: {group.summary.attendanceRate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                      {group.records.map((record) => {
                        const row = transformRecord(record);
                        if (!row) return null;
                        const statusKey = record.status || row.status;
                        const monthLabel = row.rawDate
                          ? new Date(`${row.rawDate}T12:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                          : '—';
                        return (
                          <tr key={record.id || row.rawDate} className="hover:bg-muted/30">
                            <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{monthLabel}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-foreground font-medium">
                              {row.rawDate
                                ? new Date(row.rawDate + 'T12:00:00').toLocaleDateString('en-IN', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'short',
                                  })
                                : row.date}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-foreground">
                              <div>{row.punchIn?.time || '-'}</div>
                              {row.sessionCount > 1 && (
                                <div className="text-xs text-muted-foreground">{row.sessionCount} sessions</div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-foreground">
                              {row.punchOut?.time || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-foreground">
                              {row.totalDuration}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${getRecordStatusClass(statusKey, row.status)}`}
                              >
                                {formatRecordStatus(statusKey, row.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground hidden md:table-cell max-w-[200px] truncate">
                              {row.punchIn?.location && row.punchIn.location !== 'Location not available'
                                ? row.punchIn.location
                                : row.punchOut?.location && row.punchOut.location !== 'Location not available'
                                  ? row.punchOut.location
                                  : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
          position="top-right"
          autoHideDuration={3000}
        />
      )}
    </div>
  );
};

export default AttendanceDetails;

